import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import {Amplify} from "aws-amplify";
import awsConfig from "@/aws-exports";
import { createI18n } from 'vue-i18n'

Amplify.configure(awsConfig)
import App from './App.vue'
import router from './router'
import * as Sentry from "@sentry/vue";
import { installAuthErrorBackstops } from '@/services/telemetry/backstops';
const app = createApp(App)
import 'primeicons/primeicons.css'
import ToastService from 'primevue/toastservice';

import {FloatLabel, InputText, Button, Message, Password, InputGroup, InputGroupAddon, Divider, Popover} from "primevue";
import {definePreset} from "@primeuix/themes";


app.component('FloatLabel', FloatLabel);
app.component('InputText', InputText);
app.component('Button', Button);
app.component('Message', Message);
app.component('Password', Password);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('Divider', Divider);
app.component('Popover', Popover);

const Noir = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{surface.50}',
            100: '{surface.100}',
            200: '{surface.200}',
            300: '{surface.300}',
            400: '{surface.400}',
            500: '{surface.500}',
            600: '{surface.600}',
            700: '{surface.700}',
            800: '{surface.800}',
            900: '{surface.900}',
            950: '{surface.950}'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.950}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.800}',
                    activeColor: '{primary.700}'
                },
                highlight: {
                    background: '{primary.950}',
                    focusBackground: '{primary.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                }
            },
            dark: {
                primary: {
                    color: '{primary.50}',
                    contrastColor: '{primary.950}',
                    hoverColor: '{primary.200}',
                    activeColor: '{primary.300}'
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.300}',
                    color: '{primary.950}',
                    focusColor: '{primary.950}'
                }
            }
        }
    }
});
app.use(PrimeVue, {
    theme: {
        preset: Noir,
        options: {
            darkModeSelector: "",
        }
    }
});


const availableLocales = import.meta.env.VITE_SUPPORTED_LOCALES.split(",");

const detectBrowserLanguage = () => {
    const language = window.navigator.language;
    const lang = availableLocales.includes(language)
        ? language
        : import.meta.env.VITE_DEFAULT_LOCALE;
    localStorage.setItem("locale", lang);
    return lang;
};

import en from "./locale/en.json";
import es from "./locale/es.json";
type MessageSchema = typeof en

const i18n = createI18n<[MessageSchema], 'en' | 'es'>({
    locale: localStorage.getItem("locale")
        ? `${localStorage.getItem("locale")}`
        : detectBrowserLanguage(),
    fallbackLocale: "en",
    legacy: false,
    globalInjection: true,
    messages: {en, es}
})

Sentry.init({
    app,
    dsn: "https://fa67c1c9f5ff86760a36655c5f62b68b@o4509355188944896.ingest.us.sentry.io/4509389124665344",

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        // These three are the SDK defaults, stated explicitly on purpose.
        //
        // Session replay runs on screens that display a password field, a one-time code field,
        // and — at MFASetupView.vue:52 — the TOTP shared secret as plain text, plus its QR code
        // as an <img>. Masking is what keeps all of that out of Sentry. Leaving it implicit means
        // a future "let's see what users actually typed" tweak silently exposes credentials.
        // If you ever need to turn one of these off, do it for a specific non-auth route only.
        Sentry.replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
        })
    ],
    /**
     * Strip credentials out of navigation breadcrumbs.
     *
     * RedirectUrlBuilder.appendAuthCallback puts base64'd id/access/refresh tokens into
     * ?auth-callback=, and the OAuth authorization code sits in the /api/auth/callback URL. With
     * sendDefaultPii + browserTracingIntegration, breadcrumbs record full URLs — so without this
     * the tokens themselves end up in Sentry.
     */
    beforeBreadcrumb(breadcrumb) {
        const scrub = (url?: string) =>
            url?.replace(
                /([?&](auth-callback|code|id_token|idToken|access_token|accessToken|refresh_token|refreshToken)=)[^&]*/gi,
                '$1[redacted]'
            );
        if (breadcrumb.data?.to) breadcrumb.data.to = scrub(breadcrumb.data.to);
        if (breadcrumb.data?.from) breadcrumb.data.from = scrub(breadcrumb.data.from);
        if (breadcrumb.data?.url) breadcrumb.data.url = scrub(breadcrumb.data.url);
        return breadcrumb;
    },
    beforeSend(event) {
        if (event.request?.url) {
            event.request.url = event.request.url.replace(
                /([?&](auth-callback|code|id_token|idToken|access_token|accessToken|refresh_token|refreshToken)=)[^&]*/gi,
                '$1[redacted]'
            );
        }
        return event;
    },
    // Tracing
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
        /^https:\/\/auth-api\.bono\.eco/,
        /^https:\/\/auth-api\.test\.bono\.eco/,
        /^https:\/\/cognito-idp\.us-west-1\.amazonaws\.com/,
        /^http:\/\/localhost(?::\d+)?/
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0,// If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.VITE_APP_STAGE, // dev, test, prod
    enabled: ['test', 'prod'].includes(import.meta.env.VITE_APP_STAGE),
});

app.use(i18n);
app.use(createPinia());
app.use(ToastService);
app.use(router);

/**
 * Global backstops for everything the ~20 explicit hooks miss.
 *
 * Installed LAST on purpose: with the specific hooks already in place, whatever reaches these is
 * genuinely unaccounted for, so the events stay meaningful instead of duplicating known failures.
 */
installAuthErrorBackstops(app);

app.mount('#app');
